import { configQuery, MagicConfigMixin, SessionChange } from '@gig/magic-common';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { RouteName } from '../../routes/routes.constants';
import { namespace } from 'vuex-class';
import { ACCOUNT_NAMESPACE, actions } from '../../store/modules/account';
import type { OmitFirst } from '@gig/magic-common';
import { magic } from '@gig/magic-sdk';
import type { IOffer } from '@gig/magic-sdk/dist/adapter/offers/get';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { AFFILIATE_QUERY, IP_COUNTRY_QUERY } from '@gig/magic-messages';

const accountModule = namespace(ACCOUNT_NAMESPACE);
@Component
export default class ModalPayPlayContainer extends Mixins(MagicConfigMixin) {

  @accountModule.Action('setDepositAmount')
  setDepositAmount!: OmitFirst<typeof actions.setDepositAmount>;

  @accountModule.Action('setBonusCode')
  setBonusCode!: OmitFirst<typeof actions.setBonusCode>;
  
  @Prop({ type: Array, default: () => [30, 60, 150] }) readonly presets!: number[];
  @Prop({ type: String, default: '€' }) readonly currencySymbol!: string;

  @Prop({ type: Number, default: 10 }) readonly minAmount!: number;
  @Prop({ type: Number, default: 5000 }) readonly maxAmount!: number;

  activePresetIndex: number | null = 1;
  claimBonus = false;
  isLoggedIn = false;
  offer: IOffer | null = null;
  private ipCountry: Country | null = null;
  private inputValue: string = '';
  private allowedLinks: string[] = [RouteName.HOME, RouteName.GAMES, RouteName.LIVE_CASINO, RouteName.SEARCH];
  RouteName = RouteName;
  customAmountEmpty = false;

  mounted() {
    this.setAmount(this.presets[1]);
    this.loadOffer();
  }

  @Watch('magicConfig.language', { immediate: true })
  onLanguageChange() {
    this.loadOffer();
  }

  /**
   * Parse number from input string
   * Returns number
   */
  private get amount(): number {
    const num: number = this.parseNumber(this.inputValue);
    return isFinite(num) ? num : 0;
  }

  /**
   * Check if we need to display this component
   * Returns boolean
   */
  get isDisplay(): boolean {
    const routeName = this.$route.name || '';
    const categorySlug = this.$route.params.categorySlug || '';

    return this.allowedLinks.includes(routeName) ||
      this.allowedLinks.includes(categorySlug) ||
      this.$route.name === RouteName.GAMES_SEARCH;
  }

  /**
   * Check if amount is bigger or smaller then min/max amount range
   * Returns boolean
   */
  get isValid(): boolean {
    return this.amount >= this.minAmount && this.amount <= this.maxAmount;
  }

  get claimBonusLabel(): string {
    return this.translate('pay-play.welcome-bonus.claim', 'Claim Bonus');
  }

  get bonusBoxHeading(): string {
    return this.translate('bank-id.bonus.title', 'Choose bonus');
  }

  get bonusBoxTitle(): string {
    return this.translate('pay-play.welcome-bonus.title', 'WELCOME BONUS');
  }

  get bonusBoxContent(): string {
    return this.translate(
      'pay-play.welcome-bonus.subtitle',
      `100% UP TO ${this.currencySymbol}100 + 100 FREE SPINS`,
    );
  }

  get bonusTermsPrefix(): string {
    return this.translate(
      'pay-play.welcome-bonus.terms-prefix',
      `New player offer only. Minimum deposit: ${this.currencySymbol}20. Maximum withdrawable winnings: ${this.currencySymbol}3,000. Full Terms & Conditions apply. 21+`,
    )
  }

  get bonusTermsText(): string {
    return `${this.bonusTermsPrefix}`;
  }

  @SessionChange({ immediate: true })
  onSessionChange(isLoggedIn: boolean) {
    this.isLoggedIn = isLoggedIn;
  }
  
  /**
   * Assign new preset index and update inputStr value
   * @param index 
   */
  selectPreset(index: number) {
    this.activePresetIndex = index;
    this.inputValue = this.presets[index].toFixed(2);
    this.customAmountEmpty = false;
  }

  toggleBonus() {
    this.claimBonus = !this.claimBonus;
  }

  /**
   * Return inputStr value with currency symbol
   * Returns string
   */
  get displayValue(): string {
    return this.inputValue ? `${this.inputValue} ${this.currencySymbol}` : '';
  }

  get placeholderValue(): string {
    return `0.00 ${this.currencySymbol}`;
  }

  /**
   * React on input changes, updates inputStr value and activePresetIndex
   * @param event 
   */
  onInputChange(event: Event) {
    const el = event.target as HTMLInputElement;
    const raw: string = ((event.target as HTMLInputElement)?.value ?? '');

    this.inputValue = this.normalizeAmountInput(raw);
    this.activePresetIndex = 
    this.presets.indexOf(Math.floor(Number(this.inputValue) * 100) / 100) > -1 
      ? this.presets.indexOf(Math.floor(Number(this.inputValue) * 100) / 100)
      : null;

    if (this.inputValue !== raw) {
      el.value = `${this.inputValue} ${this.currencySymbol}`;
    }
    this.customAmountEmpty = this.inputValue === '';
  }

  submit() {
    if (!this.isValid) {return};
    const inputValueNumber = Math.trunc(Number(this.inputValue) * 100) / 100;
    this.setDepositAmount(inputValueNumber);
    this.setBonusCode(this.claimBonus && this.offer ? this.offer.slug : '');
    this.$router.push({
      name: RouteName.LOGIN,
      params: {
        loginWithBank: 'true',
      },
    });
  }

  createAccount() {
    this.$router.push({
      name: RouteName.SIGN_UP
    });
  }

  /**
   * Updates activePresetIndex and inputStr value
   * @param value 
   */
  setAmount(value: number) {
    const index: number = this.presets.findIndex((preset) => Math.abs(preset - value) < 1e-6);
    this.activePresetIndex = index >= 0 ? index : null;
    this.inputValue = value.toFixed(2);
  }

  /**
   * Return value number with currency symbol
   * @param value 
   * Returns string
   */
  formatPreset(value: number): string {
    return `${value}${this.currencySymbol}`;
  }

  /**
   * Return max value of active cursor
   * Returns number
   */
  get cursorMaxPosition(): number {
    const value = this.displayValue || '';
    return Math.max(0, value.length - this.currencySymbol.length - 1);
  }

  /**
   * Updates active cursor position
   */
  fixCaret() {
    const el = this.$refs.amountInput as HTMLInputElement | undefined;
    if (!el) {return};
    const selectionStart: number = el.selectionStart ?? 0;
    const newSelectionStart: number = Math.min(selectionStart, this.cursorMaxPosition);
    if (newSelectionStart !== selectionStart ) {el.setSelectionRange(newSelectionStart, newSelectionStart)};
  }

  /**
   * Prevent inputStr value update if user click not allowed buttons 
   * @param event 
   */
  onKeydownDigitsOnly(event: KeyboardEvent) {
    const key: string = event.key;
    const allowNav: boolean = ['Backspace','Delete','ArrowLeft','ArrowRight','Home','End','Tab'].includes(key);
    const isDigit: boolean = /^[0-9]$/.test(key);
    const isDot: boolean = key === '.';

    if (isDot && (event.target as HTMLInputElement).value.includes('.')) {
      event.preventDefault();
      return;
    }
    if (!isDigit && !allowNav && !isDot) {event.preventDefault()};
  }

  /**
   * Parsing number
   * @param stringNumber 
   * Returns number 
   */
  parseNumber(stringNumber: string): number {
    if (!stringNumber) {return NaN};
    const cleaned: string = stringNumber.replace(/\s+/g, '').replace(',', '.').replace(/[^\d.]/g, '');
    const num: number = parseFloat(cleaned);
    return Number.isFinite(num) ? Math.floor(num * 100) / 100 : NaN;
  }

  private translate(key: string, fallback: string): string {
    const value = this.$t(key);
    return value.length < 1 ? fallback : String(value);
  }

  private async loadOffer() {
    const affiliateTag = await window.queryBus.query<string>(AFFILIATE_QUERY);
    const bonusCode = await this.getBonusCode();

    if (!bonusCode) {
      this.offer = null;
      return;
    }

    try {
      if (!affiliateTag) {
        this.offer = await magic.methods.offers.get(this.magicConfig.language, bonusCode);
        return;
      }

      const offers = await magic.methods.offers.getAll(this.magicConfig.language);
      this.offer = offers.filter((offer) => {
        return offer.btag === affiliateTag && offer.slug.startsWith(bonusCode);
      })[0] || null;
    } catch (e) {
      this.offer = null;
    }
  }

  private async getBonusCode(): Promise<string> {
    const bonus = await this.getBonus();
    const bonusCountry = await this.getIpCountry();

    return bonus[bonusCountry]!;
  }

  private async getBonus(): Promise<Partial<Record<Country, string>>> {
    return await configQuery('bankid.bonus') || {};
  }

  private async getIpCountry(): Promise<Country> {
    if (!this.ipCountry) {
      this.ipCountry = await window.queryBus.query<Country>(IP_COUNTRY_QUERY);
    }

    return this.ipCountry;
  }

  private normalizeAmountInput(raw: string): string {
    const cleaned = raw
      .replace(/[€\s]/g, '')
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    const dotIndex = cleaned.indexOf('.');
    if (dotIndex === -1) {
      return cleaned;
    }
    const intPart = cleaned.slice(0, dotIndex);
    const decimalPart = cleaned.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2);
    return `${intPart}.${decimalPart}`;
  }
}
